import {
  HeatMapAction,
  HeatMapState,
  initialHeatMapState,
  SET_HEATMAP_METRIC,
  SET_HEATMAP_DATE_RANGE,
  SET_HEATMAP_HIGH_COLOR,
  SET_HEATMAP_LOW_COLOR,
  SET_DISPLAY_GRADIENT_ON_GRID,
  SET_SHOW_MINI_HEAT_MAP,
  RESET_HEATMAP_STATE,
} from "./HeatMapTypes";
import {
  FETCH_PRODUCT_ANALYTICS,
  LOAD_PRODUCT_ANALYTICS,
  LoadProductAnalyticsAction,
  LoadProductAnalyticsResponseAction,
} from "../product/ProductTypes";
import {
  normalizeAnalyticsDate,
  resolveDateRange,
} from "../../utils/HeatMapUtils";

export type { HeatMapState } from "./HeatMapTypes";

export const HeatMapReducer = (
  state: HeatMapState = initialHeatMapState,
  action: HeatMapAction | { type: string },
): HeatMapState => {
  switch (action.type) {
    case SET_HEATMAP_METRIC: {
      const a = action as Extract<HeatMapAction, { type: typeof SET_HEATMAP_METRIC }>;
      return { ...state, metric: a.payload.metric };
    }
    case SET_HEATMAP_DATE_RANGE: {
      const a = action as Extract<HeatMapAction, { type: typeof SET_HEATMAP_DATE_RANGE }>;
      return { ...state, dateRange: { ...a.payload } };
    }
    case SET_HEATMAP_HIGH_COLOR: {
      const a = action as Extract<HeatMapAction, { type: typeof SET_HEATMAP_HIGH_COLOR }>;
      return { ...state, highColor: a.payload.color };
    }
    case SET_HEATMAP_LOW_COLOR: {
      const a = action as Extract<HeatMapAction, { type: typeof SET_HEATMAP_LOW_COLOR }>;
      return { ...state, lowColor: a.payload.color };
    }
    case LOAD_PRODUCT_ANALYTICS: {
      const a = action as LoadProductAnalyticsAction;
      const newStart = normalizeAnalyticsDate(a.payload?.startDate);
      const newEnd = normalizeAnalyticsDate(a.payload?.endDate);
      const resolved = resolveDateRange(
        state.dateRange.option,
        state.dateRange.customStartDate,
        state.dateRange.customEndDate,
      );
      const heatMapStart = normalizeAnalyticsDate(resolved.startDate);
      const heatMapEnd = normalizeAnalyticsDate(resolved.endDate);
      if (newStart !== heatMapStart || newEnd !== heatMapEnd) {
        return state;
      }
      const rangeChanged =
        newStart !== state.lastFetchedStartDate ||
        newEnd !== state.lastFetchedEndDate;
      return {
        ...state,
        pendingRequests: state.pendingRequests + 1,
        lastFetchedStartDate: newStart,
        lastFetchedEndDate: newEnd,
        // When the requested range changes, drop per-product freshness
        // so cells from the old range stop being treated as fresh.
        freshProductCodes: rangeChanged ? {} : state.freshProductCodes,
      };
    }
    case FETCH_PRODUCT_ANALYTICS.SUCCESS: {
      // Decrement the in-flight count AND mark this range as settled so
      // the planogram can stop treating cached values as stale.
      const settledKey = `${state.lastFetchedStartDate ?? ""}|${state.lastFetchedEndDate ?? ""}`;
      const successAction = action as LoadProductAnalyticsResponseAction;
      const products = successAction.payload?.products ?? [];
      const updatedFresh = { ...state.freshProductCodes };
      products.forEach((p) => {
        if (p?.productCode) updatedFresh[p.productCode] = true;
      });
      return {
        ...state,
        pendingRequests: Math.max(0, state.pendingRequests - 1),
        lastSettledRangeKey: settledKey,
        freshProductCodes: updatedFresh,
      };
    }
    case FETCH_PRODUCT_ANALYTICS.FAILURE:
      return state.pendingRequests > 0
        ? { ...state, pendingRequests: state.pendingRequests - 1 }
        : state;
    case SET_DISPLAY_GRADIENT_ON_GRID: {
      const a = action as Extract<
        HeatMapAction,
        { type: typeof SET_DISPLAY_GRADIENT_ON_GRID }
      >;
      return { ...state, displayGradientOnGrid: a.payload.enabled };
    }
    case SET_SHOW_MINI_HEAT_MAP: {
      const a = action as Extract<
        HeatMapAction,
        { type: typeof SET_SHOW_MINI_HEAT_MAP }
      >;
      return { ...state, showMiniHeatMap: a.payload.enabled };
    }
    case RESET_HEATMAP_STATE:
      const a = action as Extract<HeatMapAction, { type: typeof RESET_HEATMAP_STATE }>;
      // Preserve the modal config (metric / colors / date range) AND
      // both persistent toggles when either is currently on — same
      // rule for both so they stay in sync across modal close.
      // `payload` (with `displayGradientOnGrid: false`) forces a full
      // reset regardless (e.g. category change).
      return (state.displayGradientOnGrid || state.showMiniHeatMap) &&
        !a.payload
        ? {
            ...initialHeatMapState,
            metric: state.metric,
            dateRange: state.dateRange,
            highColor: state.highColor,
            lowColor: state.lowColor,
            displayGradientOnGrid: state.displayGradientOnGrid,
            showMiniHeatMap: state.showMiniHeatMap,
            freshProductCodes: state.freshProductCodes,
            lastFetchedStartDate: state.lastFetchedStartDate,
            lastFetchedEndDate: state.lastFetchedEndDate,
            lastSettledRangeKey: state.lastSettledRangeKey,
          }
        : { ...initialHeatMapState };
    default:
      return state;
  }
};
